// page-scoped dirty-state container. components that opt in (EditableText
// with a dirtyKey, EditablePill with a dirtyKey, the AssetInventoryTab
// boolean toggle) accumulate drafts here instead of saving on every blur.
// the user reviews them in DirtyChangesBar and presses Save once,
// committing every UPDATE inside a single transaction via the
// /api/batched-update endpoint.
//
// browser close / reload is intercepted via the window `beforeunload`
// event below, pure DOM API, no router dependency. in-app navigation
// (Link click, programmatic navigate) intentionally does NOT prompt. the
// provider unmounts with its host page, drafts garbage-collect, and the
// user can always navigate back to find the original values intact
// (nothing was committed). useBlocker would have given a confirmation
// modal but requires the Data Router (createBrowserRouter) which this
// app does not use, see d9d7364 for the failed first attempt.
//
// backward-compat: when no provider is mounted (modals, legacy pages),
// the context resolves to NULL_DIRTY_CONTEXT and components fall through
// to their original direct-save behavior. consumers detect this via the
// `isUnderDirtyForm` flag, true only inside a real provider.

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react'
import { request as apiRequest } from '@/lib/apiClient'

type DirtyKey = string  // `${table}:${id}:${field}`, unique per cell

export interface DirtyEntry {
  table:         string
  id:            string
  field:         string
  originalValue: unknown
  draftValue:    unknown
}

export interface BatchedOp {
  table:  string
  id:     string
  fields: Record<string, unknown>
}

export interface DirtyFormContextValue {
  isUnderDirtyForm: boolean
  context:          string  // label, e.g. "system_detail" / "asset_inventory"
  count:            number
  hasDirty:         boolean
  isSaving:         boolean
  saveError:        string | null
  entries:          ReadonlyMap<DirtyKey, DirtyEntry>

  setDraft:   (table: string, id: string, field: string, originalValue: unknown, draftValue: unknown) => void
  clearDraft: (table: string, id: string, field: string) => void
  getDraft:   (table: string, id: string, field: string) => DirtyEntry | undefined
  getOps:     () => BatchedOp[]

  saveAll:    () => Promise<{ success: boolean; error?: string }>
  discardAll: () => void
}

// sentinel context returned when no provider is mounted. all mutators are
// no-ops, isUnderDirtyForm is false so EditableText et al can branch into
// their direct-save path. frozen so no caller can accidentally mutate it.
const NULL_DIRTY_CONTEXT: DirtyFormContextValue = Object.freeze({
  isUnderDirtyForm: false,
  context:          '',
  count:            0,
  hasDirty:         false,
  isSaving:         false,
  saveError:        null,
  entries:          new Map<DirtyKey, DirtyEntry>(),
  setDraft:   () => {},
  clearDraft: () => {},
  getDraft:   () => undefined,
  getOps:     () => [],
  saveAll:    async () => ({ success: false, error: 'No DirtyFormProvider in tree' }),
  discardAll: () => {},
})

const DirtyFormContext = createContext<DirtyFormContextValue>(NULL_DIRTY_CONTEXT)

export function useDirtyForm(): DirtyFormContextValue {
  return useContext(DirtyFormContext)
}

// shallow equality good enough for the value types we actually store
// (string, boolean, number, null, and small string[] for inventory cells).
// deep equality on arbitrary nested objects isn't needed: nothing in the
// editable whitelists is structured beyond a flat array of strings.
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return a == null && b == null
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
    return true
  }
  return false
}

interface DirtyFormProviderProps {
  context:   string
  children:  React.ReactNode
  // called after a successful batched save so the host page can re-fetch.
  // receives the response payload so callers that want to reconcile state
  // optimistically can pull the rows from there instead of re-fetching.
  onSaved?:  (results: Array<{ table: string; id: string; row: any }>) => void | Promise<void>
}

export const DirtyFormProvider: React.FC<DirtyFormProviderProps> = ({ context, children, onSaved }) => {
  const [entries,   setEntries]   = useState<Map<DirtyKey, DirtyEntry>>(new Map())
  const [isSaving,  setIsSaving]  = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const setDraft = useCallback((
    table: string, id: string, field: string,
    originalValue: unknown, draftValue: unknown,
  ) => {
    const key = `${table}:${id}:${field}`
    setEntries(prev => {
      const next = new Map(prev)
      // smart-diff: if the user edited and then reverted to the original,
      // remove the entry instead of leaving a phantom "1 unsaved" in the bar.
      if (valuesEqual(draftValue, originalValue)) {
        next.delete(key)
      } else {
        next.set(key, { table, id, field, originalValue, draftValue })
      }
      return next
    })
  }, [])

  const clearDraft = useCallback((table: string, id: string, field: string) => {
    const key = `${table}:${id}:${field}`
    setEntries(prev => {
      if (!prev.has(key)) return prev
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }, [])

  const getDraft = useCallback((table: string, id: string, field: string) => {
    return entries.get(`${table}:${id}:${field}`)
  }, [entries])

  const getOps = useCallback((): BatchedOp[] => {
    // group entries by (table, id), the server endpoint expects one op
    // per row, with all changed columns folded into op.fields.
    const opsByKey = new Map<string, BatchedOp>()
    for (const e of entries.values()) {
      const groupKey = `${e.table}:${e.id}`
      let op = opsByKey.get(groupKey)
      if (!op) {
        op = { table: e.table, id: e.id, fields: {} }
        opsByKey.set(groupKey, op)
      }
      op.fields[e.field] = e.draftValue
    }
    return Array.from(opsByKey.values())
  }, [entries])

  const discardAll = useCallback(() => {
    setEntries(new Map())
    setSaveError(null)
  }, [])

  const saveAll = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (entries.size === 0) return { success: true }
    const ops = getOps()
    setIsSaving(true)
    setSaveError(null)
    try {
      // `context` (e.g. "system_detail" / "asset_inventory") rides through to
      // audit_logs.metadata so the audit trail can distinguish where a batch
      // originated without parsing the ops array. the server treats it as
      // an opaque string label.
      const { data, error } = await apiRequest<{
        success: boolean
        results: Array<{ table: string; id: string; row: any }>
      }>('POST', '/api/batched-update', { ops, context })
      if (error || !data?.success) {
        const msg = error?.message ?? 'Save failed'
        setSaveError(msg)
        return { success: false, error: msg }
      }
      setEntries(new Map())
      if (onSaved) await onSaved(data.results)
      return { success: true }
    } catch (err: any) {
      const msg = err?.message ?? 'Network error'
      setSaveError(msg)
      return { success: false, error: msg }
    } finally {
      setIsSaving(false)
    }
  }, [entries, getOps, onSaved, context])

  // browser close / reload guard. modern Chromium ignores e.returnValue
  // text and shows its built-in "Leave site?" prompt, we just need to
  // set it. in-app navigation is intentionally not blocked.
  useEffect(() => {
    if (entries.size === 0) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [entries.size])

  const value: DirtyFormContextValue = useMemo(() => ({
    isUnderDirtyForm: true,
    context,
    count:    entries.size,
    hasDirty: entries.size > 0,
    isSaving,
    saveError,
    entries,
    setDraft,
    clearDraft,
    getDraft,
    getOps,
    saveAll,
    discardAll,
  }), [context, entries, isSaving, saveError, setDraft, clearDraft, getDraft, getOps, saveAll, discardAll])

  return <DirtyFormContext.Provider value={value}>{children}</DirtyFormContext.Provider>
}
